/**
 * HH Finance - Scheduler Status Component
 *
 * Displays scheduled CFO jobs and their status
 */

import React from 'react';
import styled from 'styled-components';
import { Card } from '@shared/components/card';
import { Badge } from '@shared/components/badge';
import type { ScheduledJob, SchedulerStatus as SchedulerStatusType } from '../types';

export interface SchedulerStatusProps {
  status: SchedulerStatusType;
  jobs: ScheduledJob[];
  onRunJob?: (jobId: string) => void;
  onToggleJob?: (jobId: string, enabled: boolean) => void;
}

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary || '#1a1a1a'};
  margin: 0;
`;

const Icon = styled.span`
  font-size: 20px;
`;

const StatusBadge = styled.span<{ running: boolean }>`
  font-size: 11px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: ${({ running }) => running ? '#dcfce7' : '#fee2e2'};
  color: ${({ running }) => running ? '#16a34a' : '#dc2626'};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 8px;
  border-bottom: 2px solid ${({ theme }) => theme.border || '#e5e5e5'};
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.textSecondary || '#666'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Tr = styled.tr`
  &:hover {
    background-color: ${({ theme }) => theme.backgroundHover || '#f9fafb'};
  }
`;

const Td = styled.td`
  padding: 10px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.border || '#f0f0f0'};
  font-size: 13px;
  color: ${({ theme }) => theme.textPrimary || '#1a1a1a'};
`;

const JobName = styled.div`
  font-weight: 500;
`;

const JobDescription = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.textSecondary || '#666'};
  margin-top: 2px;
`;

const CronExpression = styled.code`
  font-family: 'SF Mono', Monaco, 'Courier New', monospace;
  font-size: 11px;
  background-color: ${({ theme }) => theme.backgroundSecondary || '#f3f4f6'};
  padding: 2px 6px;
  border-radius: 4px;
`;

const ActionButton = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.border || '#e5e5e5'};
  color: ${({ theme }) => theme.textSecondary || '#666'};
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 8px;

  &:hover {
    background-color: ${({ theme }) => theme.backgroundHover || '#f3f4f6'};
    color: ${({ theme }) => theme.textPrimary || '#1a1a1a'};
  }
`;

export const SchedulerStatus: React.FC<SchedulerStatusProps> = ({
  status,
  jobs,
  onRunJob,
  onToggleJob,
}) => {
  return (
    <Card>
      <Header>
        <TitleRow>
          <Icon>⏰</Icon>
          <Title>Scheduled CFO Jobs</Title>
        </TitleRow>
        <StatusBadge running={status.running}>
          {status.running ? 'RUNNING' : 'STOPPED'}
        </StatusBadge>
      </Header>

      <Table>
        <thead>
          <tr>
            <Th>Job</Th>
            <Th>Schedule</Th>
            <Th>Status</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <Tr key={job.id}>
              <Td>
                <JobName>{job.name}</JobName>
                <JobDescription>{job.description}</JobDescription>
              </Td>
              <Td>
                <CronExpression>{job.schedule.cronExpression}</CronExpression>
              </Td>
              <Td>
                <Badge
                  variant={job.schedule.enabled ? 'success' : 'error'}
                  size="sm"
                >
                  {job.schedule.enabled ? 'ENABLED' : 'DISABLED'}
                </Badge>
              </Td>
              <Td>
                {onRunJob && (
                  <ActionButton onClick={() => onRunJob(job.id)}>
                    Run Now
                  </ActionButton>
                )}
                {onToggleJob && (
                  <ActionButton
                    onClick={() => onToggleJob(job.id, !job.schedule.enabled)}
                  >
                    {job.schedule.enabled ? 'Disable' : 'Enable'}
                  </ActionButton>
                )}
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
};

export default SchedulerStatus;
